import { useUserInfo } from '@/domain/account/hooks';
import { type AuthUser } from '@/domain/account/types';
import { ApiError, HttpStatusCode } from '@/services/ApiError';
import { isUserAdmin } from '@/utils/permissions';
import { useSession } from 'next-auth/react';
import { createContext, type ReactNode, useMemo } from 'react';

type AuthContextType = {
  isLoggedIn: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  user: AuthUser | undefined;
  isBackendRejected: boolean;
};

type AuthContextProviderProps = {
  children: ReactNode;
};

export const AuthContext = createContext<AuthContextType>({
  isAdmin: false,
  isLoggedIn: false,
  isLoading: true,
  user: undefined,
  isBackendRejected: false,
});

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const { status, data: session } = useSession();
  const isAuthenticated = status === 'authenticated';
  const { data: user, isLoading: isUserLoading, error: userError } = useUserInfo(isAuthenticated);
  const values = useMemo(
    () => ({
      isLoggedIn: status === 'authenticated' && !!session?.user,
      isLoading: status === 'loading' || (status === 'authenticated' && isUserLoading),
      isAdmin: isUserAdmin(user),
      user,
      // Only a 403 from GET /account/user means the token authenticated but no local user resolved.
      // Any other failure (500, network) must reach the app's ordinary error handling instead of
      // telling the user their account does not exist.
      isBackendRejected: userError instanceof ApiError && userError.statusCode === HttpStatusCode.Forbidden,
    }),
    [status, session?.user, user, isUserLoading, userError],
  );

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};
